import React, { useState, useEffect, useRef } from "react";
import FaqSection from "../components/FaqSection";
import { aboutFaq } from "../seo/pageMeta";
import "./AboutPage.css";
import Footer from "../Footer";
import ContentSection1 from "../ContentSection1";
import bibaLogo from "../logo.jpeg";


// ── Slideshow data ────────────────────────────────────────────────────────────
const SLIDE_COUNT = 19;
const slides = Array.from({ length: SLIDE_COUNT }, (_, i) => `/slideshow/p${i + 1}.jpg`);

const certificates = ["c7.jpeg", "c3.jpeg", "c2.jpeg", "c4.jpeg", "c5.jpeg", "c6.jpeg", "c1.jpeg"];

const principles = [
  { title: "Customer Fascination", desc: "Knowing your customer and understanding the reason why they are even talking to us.", icon: "/icons/principles/customer-fascination.png" },
  { title: "Ownership", desc: "Be it a task, negotiation, development, hiring or knowledge sharing — owning it is the key.", icon: "/icons/principles/ownership.png" },
  { title: "Invent and Innovate", desc: "Innovation means changing the world — starting from small workflow or process improvements.", icon: "/icons/principles/invent-innovate.png" },
  { title: "Curiosity", desc: "Curiosity is the key to invention. Be curious; it generates unbiased motivation.", icon: "/icons/principles/curiosity.png" },
  { title: "Aim to Horizon", desc: "Aim for the sky so summits seem small. Setting high standards for self is the key.", icon: "/icons/principles/aim-to-horizon.png" },
  { title: "Deep Dive", desc: "Reach to the lowest task — understanding in a way to achieve mastery.", icon: "/icons/principles/deep-dive.png" },
  { title: "Disagreement & Commit", desc: "Challenge decisions respectfully. Once committed, there's no turning back.", icon: "/icons/principles/disagreement-commit.png" },
  { title: "Deliver Results", desc: "Delivery of results is the way to go — with quality and time being essential.", icon: "/icons/principles/deliver-results.png" },
  { title: "Small Changes, Bigger Impact", desc: "Improve by 1% consistently every day to achieve 100% long-term impact.", icon: "/icons/principles/small-changes-bigger-impact.png" },
  { title: "Break it Down", desc: "Big problems have bigger solutions — built from many small ones. Break it down.", icon: "/icons/principles/break-it-down.png" },
];


const leftPrinciples = principles.slice(0, 5);
const rightPrinciples = principles.slice(5, 10).reverse();


const nodeColor = () => "#8bb662";

const NAV_ITEMS = [
  { id: "about-us", label: "About Us" },
  { id: "vision-mission", label: "Vision & Mission" },
  { id: "principles", label: "Principles" },
  { id: "certifications", label: "Certifications" },
  { id: "presentation", label: "Presentation" },
];

const COMPETENCIES = [
  {
    number: "01",
    icon: "/icons/competencies/tft-display.png",
    title: "TFT Display Solutions",
    desc: "High-resolution TFT LCDs, capacitive touch panels, and custom display interfaces for industrial, medical, automotive, and consumer electronics applications."
  },
  {
    number: "02",
    icon: "/icons/competencies/embedded-design.png",
    title: "Embedded Design Services",
    desc: "Complete embedded hardware and firmware development, including PCB layout, RTOS, IoT integration, and customized electronic solutions."
  },
  {
    number: "03",
    icon: "/icons/competencies/electronics-manufacturing.png",
    title: "Electronics Manufacturing (EMS)",
    desc: "End-to-end EMS solutions including SMT assembly, testing, quality assurance, and complete box-build manufacturing."
  }
];

const whyChoose = [
  {
    icon: "/icons/why/one-roof.png",
    title: "One-Roof Solution",
    description:
      "Design, engineering and manufacturing under one roof with faster execution and better coordination."
  },
  {
    icon: "/icons/why/made-india.png",
    title: "Made in India, Global Standards",
    description:
      "Manufacturing in India with export-ready quality standards and global compliance."
  },
  {
    icon: "/icons/why/innovation.png",
    title: "Innovation Driven",
    description:
      "Engineering-first approach focused on solving complex electronics challenges."
  },
  {
    icon: "/icons/why/partnership.png",
    title: "Collaborative Approach",
    description:
      "We work as your technology partner rather than simply a manufacturing vendor."
  }
];


const WHY_BIBA = [
  {
    number: "01",
    icon: "/icons/why/one-roof.png",
    title: "One-Roof Solution",
    desc: "Design, engineering and manufacturing under one roof for faster execution and seamless collaboration."
  },
  {
    number: "02",
    icon: "/icons/why/global-standard.png",
    title: "Global Quality",
    desc: "Manufacturing aligned with international standards while proudly supporting Make in India."
  },
  {
    number: "03",
    icon: "/icons/why/innovation.png",
    title: "Innovation Driven",
    desc: "We continuously improve products through modern engineering, embedded design and smart manufacturing."
  },
  {
    number: "04",
    icon: "/icons/why/support.png",
    title: "Dedicated Support",
    desc: "Our engineering team works closely with customers from concept to production."
  },
  {
    number: "05",
    icon: "/icons/why/quality.png",
    title: "Reliable Delivery",
    desc: "Quality assurance, rigorous testing and timely delivery for every project."
  }
];


// ── Slideshow component ───────────────────────────────────────────────────────
const Presentation = () => {
  const [slideIndex, setSlideIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const timerRef = useRef(null);
  const progressRef = useRef(null);

  const startProgress = () => {
    if (!progressRef.current) return;
    progressRef.current.style.animation = "none";
    void progressRef.current.offsetWidth;
    progressRef.current.style.animation = "abProgress 3s linear forwards";
  };

  const prev = () => { setSlideIndex(i => (i - 1 + slides.length) % slides.length); startProgress(); };
  const next = () => { setSlideIndex(i => (i + 1) % slides.length); startProgress(); };

  useEffect(() => {
    if (autoplay) {
      startProgress();
      timerRef.current = setInterval(next, 3000);
    } else {
      if (progressRef.current) progressRef.current.style.animation = "none";
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [autoplay, slideIndex]);

  return (
    <div className="ab-presentation-wrap">
      <div className="ab-slide-frame">
        <img key={slideIndex} src={slides[slideIndex]} alt={`Slide ${slideIndex + 1}`} className="ab-slide-img" />
        <div className="ab-progress-bar" ref={progressRef} />
        <div className="ab-slide-controls">
          <button onClick={prev} aria-label="Previous">◀</button>
          <button className="ab-pause-btn" onClick={() => setAutoplay(a => !a)} aria-label="Pause/Play">
            {autoplay ? "⏸" : "▶"}
          </button>
          <button onClick={next} aria-label="Next">▶</button>
        </div>
      </div>
      <div className="ab-slide-counter">{slideIndex + 1} / {slides.length}</div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const AboutPage = () => {

  const [activeSection, setActiveSection] = useState("about-us");
  const [openPost, setOpenPost] = useState(null); // holds a BLOG_POSTS entry
  const [activePrinciple, setActivePrinciple] = useState(0);
  const [principlesVisible, setPrinciplesVisible] = useState(false);


  // ── Refs + state for the ring → card connector lines ──────────────────────
  const flowGridRef = useRef(null);
  const ringRef = useRef(null);
  const cardRefs = useRef({}); // { [principleNumber]: HTMLElement }
  const [connectors, setConnectors] = useState({ width: 0, height: 0, lines: [] });

  // Scroll-spy
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); }),
      { rootMargin: "-40% 0px -55% 0px" }
    );
    NAV_ITEMS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Principle diagram scroll-reveal — driven by React state so it can't be
  // clobbered by re-renders that also toggle the active-node className.
  useEffect(() => {
    const wrap = document.querySelector(".ab-flow-wrap");
    if (!wrap) return;
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { setPrinciplesVisible(true); obs.disconnect(); } }),
      { threshold: 0.12 }
    );
    obs.observe(wrap);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!principlesVisible) return;

    const compute = () => {
      const gridEl = flowGridRef.current;
      const ringEl = ringRef.current;
      if (!gridEl || !ringEl) return;

      const gridRect = gridEl.getBoundingClientRect();
      const ringRect = ringEl.getBoundingClientRect();
      const ringCenter = {
        x: ringRect.left + ringRect.width / 2 - gridRect.left,
        y: ringRect.top + ringRect.height / 2 - gridRect.top,
      };
      const ringRadius = ringRect.width / 2;

      const lines = [];
      Object.entries(cardRefs.current).forEach(([numStr, el]) => {
        if (!el) return;
        const num = Number(numStr);
        const isLeftColumn = num <= 5;
        const r = el.getBoundingClientRect();
        const target = {
          x: (isLeftColumn ? r.right : r.left) - gridRect.left,
          y: r.top + r.height / 2 - gridRect.top,
        };
        const dx = target.x - ringCenter.x;
        const dy = target.y - ringCenter.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        // Start the line at the ring's edge (not its center) so it reads as
        // "coming out of" the ring, same as the reference infographic.
        // Pulled in very slightly (2%) so anti-aliasing/rounding never leaves
        // a visible gap between the ring's edge and the line.
        const startRadius = ringRadius * 0.98;
        const x1 = ringCenter.x + (dx / dist) * startRadius;
        const y1 = ringCenter.y + (dy / dist) * startRadius;
        const ALIGN_TOLERANCE = 6;
        const isAligned = Math.abs(dy) <= ALIGN_TOLERANCE;

        let c1x, c1y, c2x, c2y;
        if (isAligned) {
          c1x = x1;
          c1y = y1;
          c2x = target.x;
          c2y = target.y;
        } else {
          const segDx = target.x - x1;
          const segDy = target.y - y1;
          const segDist = Math.sqrt(segDx * segDx + segDy * segDy) || 1;
          const bend = segDist * 0.42;
          c1x = x1 + (dx / dist) * bend;
          c1y = y1 + (dy / dist) * bend;
          c2x = isLeftColumn ? target.x + bend : target.x - bend;
          c2y = target.y;
        }

        lines.push({ num, x1, y1, x2: target.x, y2: target.y, c1x, c1y, c2x, c2y });
      });

      setConnectors({ width: gridRect.width, height: gridRect.height, lines });
    };

    const ro = new ResizeObserver(() => compute());
    if (flowGridRef.current) ro.observe(flowGridRef.current);
    if (ringRef.current) ro.observe(ringRef.current);

    // Also compute right after the reveal transition so lines appear immediately.
    const t = setTimeout(compute, 560);
    window.addEventListener("resize", compute);
    return () => {
      clearTimeout(t);
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [principlesVisible]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="ab-root">

      {/* ── Firestore-driven banner slider (home_banner collection, location: "about page Banner") ── */}
      <ContentSection1 location="about page Banner" defaultImages={[]} />

      {/* ── Sticky nav (breadcrumb + section pills, same row) ── */}
      <nav className="ab-sticky-nav">
        <div className="ab-nav-row">
          <div className="ab-breadcrumbs">
            <a href="/">Home</a>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">About</span>
          </div>

          <div className="ab-nav-inner">
            {NAV_ITEMS.map(({ id, label }) => (
              <button
                key={id}
                className={`ab-nav-pill ${activeSection === id ? "active" : ""}`}
                onClick={() => scrollTo(id)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <div className="ab-page-content">

        {/* ══ 1. ABOUT US ═══════════════════════════════════════════════ */}
        <section id="about-us" className="ab-section">
          <div className="ab-section-inner">

            <div className="ab-hero-banner">
              <div className="ab-hero-text">
                <span className="ab-badge">WHO WE ARE</span>
                <h1 className="ab-hero-title">
                  Engineering Trust.<br />
                  <span className="ab-hero-green">Building Tomorrow.</span>
                </h1>
                <p className="ab-hero-desc">
                  Founded in 2021, <strong>BIBA Electronics Pvt Ltd</strong> is a dynamic
                  technology partner specializing in <em>TFT display modules</em>,{" "}
                  <em>embedded system design</em>, and{" "}
                  <em>electronics manufacturing services (EMS)</em>.
                </p>
              </div>
              <div className="ab-hero-image">
                <img
                  src="/assets/images/about-hero-right.png"
                  alt="BIBA Electronics Innovation"
                />
              </div>
            </div>

            <div className="ab-sub-label">
              <span className="ab-sub-bar"></span>
              <span className="ab-sub-text">Our Expertise</span>
              <span className="ab-sub-bar"></span>
            </div>
            <div className="ab-competency-wrapper">

              <div className="ab-competency-image">

                <img
                  src="assets/images/competencies.png"
                  alt="BIBA Core Competencies"
                />

              </div>

              <div className="ab-competency-right">

                {COMPETENCIES.map((item) => (

                  <div className="ab-competency-card" key={item.number}>

                    <div className="ab-competency-content">

                      <div className="ab-competency-icon">
                        <img
                          src={item.icon}
                          alt={item.title}
                          loading="lazy"
                        />
                      </div>

                      <div>

                        <h3>{item.title}</h3>

                        <p>{item.desc}</p>

                      </div>

                    </div>

                  </div>

                ))}

              </div>

            </div>

            <div className="ab-why-dark-section">
              <div className="ab-why-dark-inner">

                <div className="ab-sub-label">
                  <span className="ab-sub-bar"></span>
                  <span className="ab-sub-text">WHY BIBA?</span>
                  <span className="ab-sub-bar"></span>
                </div>
                <div className="ab-why-modern">

                  {/* Left Side */}

                  <div className="ab-why-left">
                    <h2 className="ab-why-heading">
                      Trusted Engineering.
                      <br />
                      <span className="ab-why-heading-shift">Proven Manufacturing.</span>
                    </h2>
                    <p className="ab-why-text">
                      BIBA Electronics delivers complete electronic product
                      development, quality manufacturing and reliable support
                      under one roof.
                    </p>

                    <div className="ab-why-collage">

                      <div className="ab-why-collage-main">
                        <img src="/assets/images/why-biba-1.jpg" alt="BIBA Electronics manufacturing" />
                      </div>

                      <div className="ab-why-collage-accent ab-why-collage-accent--top">
                        <img src="/assets/images/why-biba-2.jpg" alt="BIBA Electronics engineering" />
                      </div>

                      <div className="ab-why-collage-accent ab-why-collage-accent--bottom">
                        <img src="/assets/images/why-biba-3.jpg" alt="BIBA Electronics quality testing" />
                      </div>

                    </div>

                  </div>

                  {/* Right Side */}

                  <div className="ab-why-right">

                    {whyChoose.map((item, index) => (

                      <div
                        className="ab-why-feature"
                        key={index}
                      >

                        <div className="ab-why-feature-icon">
                          <img src={item.icon} alt={item.title} loading="lazy" />
                        </div>

                        <div>

                          <h3>{item.title}</h3>

                          <p>{item.description}</p>

                        </div>

                      </div>

                    ))}

                  </div>

                </div>

              </div>
            </div>
          </div>
        </section>

        {/* ══ 2. VISION & MISSION ═══════════════════════════════════════ */}
        <section id="vision-mission" className="ab-section ab-section--alt">
          <div className="ab-section-inner">
            <div className="ab-section-head">
              <span className="ab-badge">PURPOSE</span>
              <h2 className="ab-section-title">Vision & Mission</h2>
            </div>
            {/* 
            <div className="ab-vm-grid">
              <div className="ab-vm-card">
                <div className="ab-vm-icon-wrap"><span className="ab-vm-icon">🔭</span></div>
                <h3 className="ab-vm-title">Our Vision</h3>
                <p className="ab-vm-body">
                  We strive to lead the <strong>Indian market</strong> in display solutions, HMI systems, and embedded technology services by combining innovation with engineering excellence. Our focus is on building custom embedded solutions, industrial display systems, and smart electronic products that meet global quality standards.
                  With a strong commitment to <strong>Make in India</strong>, we are dedicated to delivering cost-effective, scalable, and high-precision manufacturing solutions for industries such as automation, IoT, consumer electronics, and industrial control systems.
                </p>
              </div>
              <div className="ab-vm-card ab-vm-card--mission">
                <div className="ab-vm-icon-wrap"><span className="ab-vm-icon">🚀</span></div>
                <h3 className="ab-vm-title">Our Mission</h3>
                <ul className="ab-list">
                  <li>Deliver high-performance TFT displays and embedded systems that meet global standards and local needs.</li>
                  <li>Integrate design, manufacturing, and compliance seamlessly — ensuring reliability, speed, and strategic value.</li>
                  <li>Champion Make in India by fostering indigenous innovation, ethical sourcing, and sustainable growth.</li>
                  <li>Support clients with proactive service, technical expertise, and collaborative problem-solving at every stage.</li>
                </ul>
              </div>
            </div> */}
            <div className="ab-vm-stack">
              <div className="ab-vm-card">
                <div className="ab-vm-photo">
                  <img src="/assets/images/vision.png" alt="BIBA Vision" />
                </div>
                <div className="ab-vm-description">
                  <div className="ab-vm-title-row">
                    <img src="/icons/vision-mission/visionIcon.png" alt="" className="ab-vm-title-icon" />                    <h2 className="ab-vm-title">Our Vision</h2>
                  </div>
                  <p className="ab-vm-body">
                    We strive to lead the <strong>Indian market</strong> in display solutions, HMI systems, and embedded technology services by combining innovation with engineering excellence. Our focus is on building custom embedded solutions, industrial display systems, and smart electronic products that meet global quality standards.
                    With a strong commitment to <strong>Make in India</strong>, we are dedicated to delivering cost-effective, scalable, and high-precision manufacturing solutions for industries such as automation, IoT, consumer electronics, and industrial control systems.
                  </p>
                </div>
              </div>

              <div className="ab-vm-card ab-vm-card--alt">
                <div className="ab-vm-photo">
                  <img src="/assets/images/mission.png" alt="BIBA Mission" />
                </div>
                <div className="ab-vm-description">
                  <div className="ab-vm-title-row">
                    <img src="/icons/vision-mission/missionIcon.png" alt="" className="ab-vm-title-icon" />
                    <h2 className="ab-vm-title">Our Mission</h2>
                  </div>
                  <ul className="ab-list ab-vm-body">
                    <li>Deliver high-performance TFT displays and embedded systems that meet global standards and local needs.</li>
                    <li>Integrate design, manufacturing, and compliance seamlessly — ensuring reliability, speed, and strategic value.</li>
                    <li>Champion Make in India by fostering indigenous innovation, ethical sourcing, and sustainable growth.</li>
                    <li>Support clients with proactive service, technical expertise, and collaborative problem-solving at every stage.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══ 3. PRINCIPLES ════════════════════════════════════════════ */}
        <section id="principles" className="ab-section">
          <div className="ab-section-inner">
            <div className="ab-principles-panel">
              <div className="ab-principles-panel-inner">

                <div className="ab-section-head">
                  <span className="ab-badge">HOW WE WORK</span>
                  <h2 className="ab-section-title">BIBA Principles</h2>
                </div>
                <div className={`ab-flow-wrap ${principlesVisible ? "ab-visible" : ""}`}>
                  <div className="ab-flow-grid" ref={flowGridRef}>
                    <svg
                      className="ab-flow-connector-svg"
                      width={connectors.width}
                      height={connectors.height}
                      viewBox={`0 0 ${connectors.width} ${connectors.height}`}
                      aria-hidden="true"
                    >
                      {connectors.lines.map((l) => (
                        <g
                          key={l.num}
                          className="ab-connector-anim"
                          style={{ "--ab-connector-delay": `${300 + (l.num - 1) * 60}ms` }}
                        >
                          <path
                            d={`M ${l.x1} ${l.y1} C ${l.c1x} ${l.c1y}, ${l.c2x} ${l.c2y}, ${l.x2} ${l.y2}`}
                            fill="none"
                            stroke={nodeColor(l.num)}
                            strokeWidth={1.6}
                            strokeDasharray="4 4"
                            opacity={0.65}
                          />
                        </g>
                      ))}
                    </svg>

                    {/* ── Left column: principles 01–05, top to bottom ── */}
                    <div className="ab-flow-col ab-flow-col--left">
                      {leftPrinciples.map(({ title, desc, icon }, idx) => {
                        const num = idx + 1;
                        const isActive = activePrinciple === idx;
                        return (
                          <div
                            key={num}
                            className={`ab-flow-row ab-pcolor${((num - 1) % 5) + 1} ${isActive ? "ab-flow-row--active" : ""}`}
                            style={{ transitionDelay: principlesVisible ? `${idx * 70}ms` : "0ms" }}
                            onMouseEnter={() => setActivePrinciple(idx)}
                            onFocus={() => setActivePrinciple(idx)}
                          >
                            <div
                              className="ab-flow-card"
                              tabIndex={0}
                              ref={(el) => (cardRefs.current[num] = el)}
                            >
                              <span className="ab-flow-card-num">{String(num).padStart(2, "0")}</span>

                              <div className="ab-flow-card-content">
                                <div className="ab-flow-card-icon-wrap">
                                  <img
                                    src={icon}
                                    alt=""
                                    className="ab-flow-card-icon"
                                    loading="lazy"
                                  />
                                </div>

                                <div className="ab-flow-card-text">
                                  <div className="ab-flow-card-title">
                                    {title}
                                  </div>

                                  <p className="ab-flow-card-desc">
                                    {desc}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* ── Center hub: segmented ring with BIBA logo ── */}
                    <div className="ab-flow-ring" ref={ringRef}>
                      <div className="ab-flow-ring-inner">
                        <img src={bibaLogo} alt="BIBA" className="ab-flow-ring-logo" />
                        <span className="ab-flow-ring-caption">10 Principles</span>
                      </div>
                    </div>

                    {/* ── Right column: principles 10 down to 06, top to bottom ── */}
                    <div className="ab-flow-col ab-flow-col--right">
                      {rightPrinciples.map(({ title, desc, icon }, idx) => {
                        const num = 10 - idx;
                        const dataIndex = num - 1;
                        const isActive = activePrinciple === dataIndex;
                        return (
                          <div
                            key={num}
                            className={`ab-flow-row ab-pcolor${((num - 1) % 5) + 1} ${isActive ? "ab-flow-row--active" : ""}`}
                            style={{ transitionDelay: principlesVisible ? `${idx * 70}ms` : "0ms" }}
                            onMouseEnter={() => setActivePrinciple(dataIndex)}
                            onFocus={() => setActivePrinciple(dataIndex)}
                          >
                            <div
                              className="ab-flow-card"
                              tabIndex={0}
                              ref={(el) => (cardRefs.current[num] = el)}
                            >
                              <span className="ab-flow-card-num">{String(num).padStart(2, "0")}</span>

                              <div className="ab-flow-card-content">
                                <div className="ab-flow-card-icon-wrap">
                                  <img
                                    src={icon}
                                    alt=""
                                    className="ab-flow-card-icon"
                                    loading="lazy"
                                  />
                                </div>

                                <div className="ab-flow-card-text">
                                  <div className="ab-flow-card-title">
                                    {title}
                                  </div>

                                  <p className="ab-flow-card-desc">
                                    {desc}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Mobile fallback — simple stacked list, ring diagram is desktop-only */}
                  <div className="ab-radial-fallback-list">
                    {principles.map(({ title, desc, icon }, i) => (
                      <div key={i} className={`ab-radial-fallback-row ab-pcolor${(i % 5) + 1}`}>
                        <span className="ab-radial-fallback-num">{String(i + 1).padStart(2, "0")}</span>
                        <div className="ab-radial-fallback-head">
                          <img src={icon} alt="" className="ab-radial-fallback-icon" loading="lazy" />
                          <div className="ab-radial-fallback-title">{title}</div>
                        </div>
                        <p className="ab-radial-fallback-desc">{desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══ 4. CERTIFICATIONS ════════════════════════════════════════ */}
        <section id="certifications" className="ab-section ab-section--alt">
          <div className="ab-section-inner">
            <div className="ab-section-head">
              <span className="ab-badge">TRUST & QUALITY</span>
              <h2 className="ab-section-title">Certifications</h2>
              <p className="ab-section-desc">Our credentials reflect our commitment to global quality and compliance standards.</p>
            </div>
            <div className="ab-cert-grid">
              {certificates.map((img, i) => (
                <div className="ab-cert-card" key={i}>
                  <img src={`/certificates/${img}`} alt={`Certificate ${i + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ 5. PRESENTATION ══════════════════════════════════════════ */}
        <section id="presentation" className="ab-section">
          <div className="ab-section-inner">
            <div className="ab-section-head">
              <span className="ab-badge">COMPANY DECK</span>
              <h2 className="ab-section-title">BIBA Presentation</h2>
              <p className="ab-section-desc">An overview of who we are, what we do, and where we're headed.</p>
            </div>
            <Presentation />
          </div>
        </section>

      <FaqSection faq={aboutFaq} />

      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;